import { handleResult } from '@/utils/handleResult'
import { easybackClient } from './api'
import { cacheService, CACHE_TTL } from './cacheService'
import type { Subject, Topic, Lesson, Course, Category, CourseSubject } from '@/types/index'
import { enrollmentService } from './enrollmentService'


export const LESSON_LIST_FIELDS = ['id', 'name', 'description', 'content_type', 'duration_minutes', 'thumbnail_url', 'sort_order', 'topic_id', 'is_published', 'quiz_id', 'created_at', 'updated_at']
export const LESSON_FULL_FIELDS = [...LESSON_LIST_FIELDS, 'content_data', 'content_url', 'prerequisites', 'learning_objectives', 'quiz_id', 'generation_meta']

/** A lesson enriched with its topic + subject metadata, for grouped rendering. */
export type CourseContentItem = Lesson & {
  topic_name: string
  topic_sort_order: number
  subject_id: number
  subject_name: string
  subject_sort_order: number
}


export const contentService = {
  getAllSubjects: async (examType?: 'tyt' | 'ayt') => {
    const cacheKey = 'subjects_all'
    const cached = cacheService.get<Subject[]>(cacheKey)
    if (cached) {
      if (examType) {
        return cached.filter(s => s.exam_type === examType)
      }
      return cached
    }

    const result = await easybackClient.list<Subject>('subjects', { sort: { sort_order: 'asc' } })
    const data = handleResult(result, 'getAllSubjects')
    const sortedData = data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    cacheService.set(cacheKey, sortedData, CACHE_TTL.EXTREMELY_LONG)

    if (examType) {
      return sortedData.filter(s => s.exam_type === examType)
    }

    return sortedData
  },

  getSubjects: async (examType?: 'tyt' | 'ayt') => {
    return contentService.getAllSubjects(examType)
  },

  getSubject: async (id: number) => {
    const allSubjects = await contentService.getAllSubjects()
    const subject = allSubjects.find(s => s.id === id)
    if (!subject) {
      console.error('[contentService] getSubject failed: subject not found')
      throw new Error('Ders bulunamadı')
    }
    return subject
  },

  getAllTopics: async (subjectId?: number) => {
    const cacheKey = 'topics_all'
    const cached = cacheService.get<Topic[]>(cacheKey)
    if (cached) {
      if (subjectId) {
        return cached.filter(t => t.subject_id === subjectId)
      }
      return cached
    }

    const result = await easybackClient.list<Topic>('topics', { sort: { sort_order: 'asc' } })
    const data = handleResult(result, 'getAllTopics')
    const sortedData = data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    cacheService.set(cacheKey, sortedData, CACHE_TTL.LONG)

    if (subjectId) {
      return sortedData.filter(t => t.subject_id === subjectId)
    }

    return sortedData
  },

  getTopic: async (id: number) => {
    const allTopics = await contentService.getAllTopics()
    const topic = allTopics.find(t => t.id === id)
    if (!topic) {
      console.error('[contentService] getTopic failed: topic not found')
      throw new Error('Konu bulunamadı')
    }
    return topic
  },

  getLessons: async (topicId: number) => {
    const allLessons = await contentService.getAllLessons()
    const filteredLessons = allLessons
      .filter(l => l.topic_id === topicId)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))

    return filteredLessons
  },

  getLesson: async (id: number) => {
    const cacheKey = `lesson_${id}`
    const cached = cacheService.get<Lesson>(cacheKey)
    if (cached) return cached

    const result = await easybackClient.list<Lesson>('lessons', {
      filter: { id: { eq: id }, is_published: { eq: true } },
      limit: 1,
      fields: LESSON_FULL_FIELDS
    })
    if (!result.success || !result.data || result.data.length === 0) {
      console.error('[contentService] getLesson failed:', result.error?.message)
      throw new Error(result.error?.message || 'Ders içeriği bulunamadı')
    }
    const lesson = result.data[0]
    cacheService.set(cacheKey, lesson, CACHE_TTL.LONG)
    return lesson
  },

  getCourses: async (examType?: 'tyt' | 'ayt' | 'lise' | 'ortaokul') => {
    const result = await easybackClient.list<Course>('courses', examType ? { filter: { course_type: { eq: examType.toUpperCase() as 'TYT' | 'AYT' | 'Lise' | 'Ortaokul' } } } : {})
    console.log('[contentService] getCourses result:', result)
    return handleResult(result, 'getCourses')
  },

  getCourse: async (id: number) => {
    const result = await easybackClient.get<Course>('courses', id)
    console.log('[contentService] getCourse result:', result)
    if (!result.success) {
      console.error('[contentService] getCourse failed:', result.error?.message)
      throw new Error(result.error?.message || 'Kurs bulunamadı')
    }
    return result.data?.[0]
  },

  getCourseContents: async (courseId: number): Promise<CourseContentItem[]> => {
    try {
      const courseSubjectsResult = await easybackClient.list<CourseSubject>('course_subjects', {
        filter: { course_id: { eq: courseId } },
        sort: { sort_order: 'asc' }
      })
      console.log('[contentService] getCourseContents course_subjects result:', courseSubjectsResult)

      if (!courseSubjectsResult.success || !courseSubjectsResult.data || courseSubjectsResult.data.length === 0) {
        if (!courseSubjectsResult.success) {
          console.error('[contentService] getCourseContents failed:', courseSubjectsResult.error?.message)
        }
        return []
      }

      const subjectIds = courseSubjectsResult.data.map(cs => cs.subject_id)

      const topicsResult = await easybackClient.list<any>('topics', {
        filter: { subject_id: { in: subjectIds } },
        sort: { sort_order: 'asc' }
      })
      console.log('[contentService] getCourseContents topics result:', topicsResult)

      const topics = topicsResult.data || []
      const topicIds = topics.map(t => t.id)

      // Fetch subject names for the course's subjects
      const subjectsResult = await easybackClient.list<Subject>('subjects', {
        filter: { id: { in: subjectIds } },
      })
      const subjectNameMap = new Map<number, string>(
        (subjectsResult.data || []).map(s => [s.id, s.name])
      )

      const lessons = (await contentService.getAllLessons()) .filter(l=>topicIds.includes(l.topic_id))

      const subjectSortOrderMap = new Map<number, number>(
        courseSubjectsResult.data.map(cs => [cs.subject_id, cs.sort_order])
      )

      const topicSortOrderMap = new Map<number, number>(
        topics.map(t => [t.id, t.sort_order])
      )

      const lessonsWithOrder = lessons
        .map(lesson => {
          const topic = topics.find(t => t.id === lesson.topic_id)
          if (!topic) return null

          const subjectSortOrder = subjectSortOrderMap.get(topic.subject_id) || 0
          const topicSortOrder = topicSortOrderMap.get(lesson.topic_id) || 0

          return {
            ...lesson,
            sort_order: (subjectSortOrder * 10000) + (topicSortOrder * 100) + (lesson.sort_order || 0),
            topic_name: topic.name,
            topic_sort_order: topic.sort_order || 0,
            subject_id: topic.subject_id,
            subject_name: subjectNameMap.get(topic.subject_id) || '',
            subject_sort_order: subjectSortOrder,
          }
        })
        .filter((l): l is CourseContentItem => l !== null)
        .sort((a, b) => a.sort_order - b.sort_order)

      return lessonsWithOrder
    } catch (err) {
      console.error('[contentService] getCourseContents error:', err)
      return []
    }
  },

  getCategories: async () => {
    const result = await easybackClient.list<Category>('categories', {
      filter: { is_active: { eq: true } },
      sort: { sort_order: 'asc' }
    })
    console.log('[contentService] getCategories result:', result)
    return handleResult(result, 'getCategories')
  },

  getCategory: async (id: number) => {
    const result = await easybackClient.get<Category>('categories', id)
    return result.data?.[0]
  },

  getCoursesByCategory: async (categoryId: number) => {
    const result = await easybackClient.list<Course>('courses', {
      filter: {
        category_id: { eq: categoryId },
        is_published: { eq: true }
      },
      sort: { created_at: 'desc' }
    })
    return result.data || []
  },

  getNextLesson: async (currentLessonId: number, topicId:number) => {
    const lessons = (await contentService.getAllLessons()).filter(l=>l.topic_id==topicId)
    const curIndex = lessons.findIndex((l)=>l.id==currentLessonId)
    return lessons[curIndex + 1] ?? null
  },

  getLessonsByTopic: async (topicId: number, include_content=false) => {
    const allLessons = await contentService.getAllLessons()
    const topicLessons = allLessons
      .filter(l => l.topic_id === topicId && l.is_published)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))

    if (!include_content) {
      return topicLessons
    }

    const lessonsWithContent = await Promise.all(
      topicLessons.map(async lesson => {
        const fullLesson = await contentService.getLesson(lesson.id)
        return fullLesson || lesson
      })
    )

    return lessonsWithContent
  },

  getLessonCourses: async (lessonId: number): Promise<Course[]> => {
    try {
      const lesson = (await contentService.getAllLessons()).find(l=>l.id==lessonId)
      const topicResult = await easybackClient.list<any>('topics', {
        filter: { id: { eq: lesson.topic_id } },
        limit: 1
      })

      if (!topicResult.success || !topicResult.data || topicResult.data.length === 0) {
        return []
      }

      const topic = topicResult.data[0]
      const subjectId = topic.subject_id

      const courseSubjectsResult = await easybackClient.list<CourseSubject>('course_subjects', {
        filter: { subject_id: { eq: subjectId } }
      })

      if (!courseSubjectsResult.success || !courseSubjectsResult.data || courseSubjectsResult.data.length === 0) {
        return []
      }

      const courseIds = courseSubjectsResult.data.map(cs => cs.course_id)
      const coursesResult = await easybackClient.list<Course>('courses', {
        filter: { id: { in: courseIds } }
      })

      return coursesResult.data || []
    } catch (err) {
      console.error('[contentService] getLessonCourses error:', err)
      return []
    }
  },

  getLessonsAccess: async (studentId: number, lessonIds: number[]): Promise<Map<number, boolean>> => {
    try {
      const allLessons = await contentService.getAllLessons()
      const topicIds = new Set(allLessons.map(l => l.topic_id))
      const topics = (await contentService.getAllTopics()).filter(t=>topicIds.has(t.id) )
      const enrollments = await enrollmentService.getActiveEnrollments(studentId)
      const enrolledCourseIds = new Set(enrollments.map(e => e.course_id))
      const allowedTopicIds = topics.filter(t=>enrolledCourseIds.has(t.subject_id)).map(t=>t.id)
      const allowedLessons = allLessons.filter(l=>allowedTopicIds.includes(l.topic_id)) 
      return new Map(lessonIds.map(id => [id, allowedLessons.find(l=>l.id==id)?true:false]))
    } catch (err) {
      console.error('[contentService] getLessonsAccess error:', err)
      return new Map(lessonIds.map(id => [id, false]))
    }
  },

  hasAccessToLesson: async (studentId: number, lessonId: number): Promise<boolean> => {
    const accessMap = await contentService.getLessonsAccess(studentId, [lessonId])
    return accessMap.get(lessonId) || false
  },

  invalidateSubjectsCache: () => {
    cacheService.invalidate('subjects_all')
  },

  invalidateTopicsCache: () => {
    cacheService.invalidate('topics_all')
  },

  getTopicLessonCounts: async (): Promise<Record<number, number>> => {
    const allLessons = await  contentService.getAllLessons()
    const counts: Record<number, number> = {}
    for (const lesson of allLessons || []) {
      counts[lesson.topic_id] = (counts[lesson.topic_id] || 0) + 1
    }
    return counts
  },

  getAllLessons: async () => {
    const table = `lessons_public`
    const cacheKey = `lessons_all_list_${table}`
    const cached = cacheService.get<Lesson[]>(cacheKey)
    if (cached) return cached

    const result = await easybackClient.list<Lesson>(table, {
      fields: LESSON_LIST_FIELDS,
      sort: { sort_order: 'asc' }
    })

    const data = handleResult(result, 'getAllLessons')
    cacheService.set(cacheKey, data, CACHE_TTL.LONG)
    return data
  },

  invalidateLessonsCache: () => {
    cacheService.invalidate('lessons_all_list')
    cacheService.invalidatePattern('lessons_topic_')
    cacheService.invalidatePattern('lesson_')
  },

  invalidateTopicLessonsCache: (topicId: number) => {
    cacheService.invalidate(`lessons_topic_${topicId}`)
  },

  invalidateLessonCache: (lessonId: number) => {
    cacheService.invalidate(`lesson_${lessonId}`)
  },

  getCourseSubjectsMap: async (): Promise<Map<number, number[]>> => {
    const cacheKey = 'course_subjects_map'
    const cached = cacheService.get<Map<number, number[]>>(cacheKey)
    if (cached) return cached

    const result = await easybackClient.list<CourseSubject>('course_subjects')
    const data = result.data || []

    const map = new Map<number, number[]>()
    for (const cs of data) {
      if (!map.has(cs.subject_id)) {
        map.set(cs.subject_id, [])
      }
      map.get(cs.subject_id)!.push(cs.course_id)
    }

    cacheService.set(cacheKey, map, CACHE_TTL.LONG)
    return map
  },

  getLessonsBySubject: async (subjectId: number): Promise<Lesson[]> => {
    const [allLessons, allTopics] = await Promise.all([
      contentService.getAllLessons(),
      contentService.getAllTopics()
    ])

    const subjectTopics = allTopics.filter((t) => t.subject_id === subjectId)
    const topicIds = subjectTopics.map((t) => t.id)

    return allLessons.filter((l) => topicIds.includes(l.topic_id))
  },

  getLessonByTopicAndSubject: async (lessonId: number): Promise<{ lesson: Lesson | null; topic: Topic | null; subjectId: number | null } | null> => {
    const [allLessons, allTopics] = await Promise.all([
      contentService.getAllLessons(),
      contentService.getAllTopics()
    ])

    const lesson = allLessons.find((l) => l.id === lessonId)
    if (!lesson) return null

    const topic = allTopics.find((t) => t.id === lesson.topic_id)
    if (!topic) return null

    const result = {
      lesson,
      topic,
      subjectId: topic.subject_id
    }
    return result
  },

  invalidateCourseSubjectsCache: (): void => {
    cacheService.invalidate('course_subjects_map')
  },
}
